
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import ErrorTextHelper from "./ErrorTextHelper";
import ReactQuill, { Quill } from "react-quill";
import { Controller } from "react-hook-form";
import "react-quill/dist/quill.snow.css";
import "quill-mention/dist/quill.mention.css";
import "quill-mention/autoregister";
import { getMentioned } from "../../services/feedPage.services";
import { stripHTMLTags } from "../../utils/helper";
import { FlashMessage } from ".";
import articleEditorIcons from "./ArticleTextEditor.icons";
import ArticleDropZone from "./ArticleDropZone";
import downArrow from "../../assets/svg/editor/down-arrow.svg";
import QuillLinkDialog, {
  bindQuillLinkDialog,
  LinkDialogPayload,
} from "./QuillLinkDialog";

const BlockEmbed = Quill.import("blots/block/embed");

const QuillIcons = Quill.import("ui/icons") as Record<string, any>;
QuillIcons.bold = articleEditorIcons.bold;
QuillIcons.italic = articleEditorIcons.italic;
QuillIcons.underline = articleEditorIcons.underline;
QuillIcons.strike = articleEditorIcons.strike;
QuillIcons.list = articleEditorIcons.list;
QuillIcons.link = articleEditorIcons.link;
QuillIcons.image = articleEditorIcons.image;

interface MediaItem {
  id: string;
  file: File;
  blobUrl: string;
  type: 'image' | 'video';
  insertIndex: number;
  timestamp: number;
  thumbnail?: string;
  /** Keeps the unmodified image available when an inline image is edited again. */
  originalFile?: File;
  originalBlobUrl?: string;
  editHistory?: InlineMediaEditHistory;
}

interface InlineMediaEditHistory {
  filterBrightness: number;
  filterContrast: number;
  filterSaturate: number;
  filterGrayscale: number;
  adjustBrightness: number;
  adjustContrast: number;
  adjustSaturate: number;
  adjustGrayscale: number;
  rotate: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  zoom: number;
  straighten: number;
  selectedFilter: string;
}

class MediaImageBlot extends BlockEmbed {
  static blotName = "mediaImage";
  static tagName = "div";
  static className = "ql-image-wrapper";

  static create(value: { url: string; mediaId: string }) {
    const node = super.create() as HTMLElement;
    node.classList.add("ql-media-wrapper");
    node.classList.add("ql-image-wrapper");
    node.setAttribute("data-media-id", value.mediaId);
    node.setAttribute("data-media-type", "image");

    const img = document.createElement("img");
    img.setAttribute("src", value.url);
    img.setAttribute("alt", "");
    img.style.display = "block";
    img.style.width = "100%";
    img.style.height = "auto";

    const editBtn = document.createElement("button");
    editBtn.className = "ql-media-edit article-media-edit-button";
    editBtn.type = "button";
    editBtn.setAttribute("contenteditable", "false");
    editBtn.setAttribute("aria-label", "Edit image");
    editBtn.innerHTML =
      '<svg class="image-edit-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path></svg>';

    const btn = document.createElement("button");
    btn.className = "ql-media-delete media-delete-button";
    btn.type = "button";
    btn.setAttribute("contenteditable", "false");
    btn.innerHTML =
      '<svg class="delete-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>';

    node.appendChild(img);
    node.appendChild(editBtn);
    node.appendChild(btn);
    return node;
  }

  static value(node: HTMLElement) {
    const img = node.querySelector("img");
    const mediaId = node.getAttribute("data-media-id");
    return img && mediaId ? { url: img.getAttribute("src") || "", mediaId } : "";
  }
}

// VIDEO blot
class MediaVideoBlot extends BlockEmbed {
  static blotName = "mediaVideo";
  static tagName = "div";
  static className = "ql-video-wrapper";

  static create(value: { url: string; mediaId: string }) {
    const node = super.create() as HTMLElement;
    node.classList.add("ql-media-wrapper");
    node.classList.add("ql-video-wrapper");
    node.setAttribute("data-media-id", value.mediaId);
    node.setAttribute("data-media-type", "video");

    const video = document.createElement("video");
    video.setAttribute("src", value.url);
    video.setAttribute("controls", "true");
    video.setAttribute("preload", "metadata");
    video.style.display = "block";
    video.style.width = "100%";
    video.style.height = "auto";

    const btn = document.createElement("button");
    btn.className = "ql-media-delete";
    btn.type = "button";
    btn.setAttribute("contenteditable", "false");
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path></svg>';

    node.appendChild(video);
    node.appendChild(btn);
    return node;
  }

  static value(node: HTMLElement) {
    const video = node.querySelector("video");
    const mediaId = node.getAttribute("data-media-id");
    return video && mediaId ? { url: video.getAttribute("src") || "", mediaId } : "";
  }
}

Quill.register(MediaImageBlot);
Quill.register(MediaVideoBlot);


interface ExternalLabel {
  label?: string;
  className?: string;
  [key: string]: any;
}
type Mention = { value: string; id: number };

interface RichTextEditorProps {
  name: string;
  control?: any;
  externalLabel?: ExternalLabel;
  validationObj?: object;
  showError?: boolean;
  className?: string;
  placeholder?: string;
  showCount?: boolean;
  maxLimit?: number;
  counterAlign?: "left" | "center" | "right";
  useMention?: boolean;
  AIflag?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onMediaChange?: (mediaItems: MediaItem[]) => void;
  onPrepareFormData?: (callback: (formData?: FormData) => FormData) => void;
  setOverflow?: (overflow: boolean) => void;
  rules?: object;
  defaultValue?: string;
  initialMediaItems?: MediaItem[];
  contentMediaProcessed?: any;
  editorHeight?: string;
  autoGrow?: boolean;
  [key: string]: any;
}

const ArticleTextEditor: React.FC<RichTextEditorProps> = ({
  name,
  control,
  validationObj,
  className = "",
  placeholder = "",
  showError = true,
  showCount = false,
  maxLimit = 1000,
  counterAlign = "right",
  useMention = false,
  AIflag = false,
  externalLabel,
  onMediaChange,
  onPrepareFormData,
  initialMediaItems,
  contentMediaProcessed,
  editorHeight = "400px",
  autoGrow = false,
  ...props
}) => {
  const labelText = externalLabel?.label || "";
  const labelClassName = externalLabel?.className || "";
  const errorMessage = _.get(validationObj, `${name}.message`, null);

  const reactQuillRef = useRef<any>(null);
  const ignoreNextChangeRef = useRef(false);
  const searchDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const mediaItemsRef = useRef<Map<string, MediaItem>>(new Map());
  const initializedRef = useRef(false);
  const [editingMediaId, setEditingMediaId] = useState<string | null>(null);
  const [linkDialog, setLinkDialog] = useState<LinkDialogPayload | null>(null);

  const resizeEditor = useCallback(() => {
    if (!autoGrow) return;
    const quill = reactQuillRef.current?.getEditor?.();
    const editor = quill?.root as HTMLElement | undefined;
    if (!editor) return;

    // Keep the reader at the current caret/viewport position while the editor
    // grows. Collapsing the editor to 0px first causes the browser's scroll
    // anchoring to jump back to the top of a long article.
    const windowScroll = typeof window === "undefined"
      ? null
      : { left: window.scrollX, top: window.scrollY };
    const scrollParents: Array<{ element: HTMLElement; left: number; top: number }> = [];
    let parent = editor.parentElement;
    while (parent) {
      const overflowY = window.getComputedStyle(parent).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        scrollParents.push({ element: parent, left: parent.scrollLeft, top: parent.scrollTop });
      }
      parent = parent.parentElement;
    }

    editor.style.height = "auto";
    const nextHeight = `${editor.scrollHeight}px`;
    if (editor.style.height !== nextHeight) editor.style.height = nextHeight;

    const restoreScrollPosition = () => {
      scrollParents.forEach(({ element, left, top }) => {
        element.scrollLeft = left;
        element.scrollTop = top;
      });
      if (windowScroll) window.scrollTo(windowScroll.left, windowScroll.top);
    };
    restoreScrollPosition();
    requestAnimationFrame(restoreScrollPosition);
  }, [autoGrow]);

  // useEffect(() => {
  //   return () => {
  //     mountedRef.current = false;
  //     if (searchDelayRef.current) clearTimeout(searchDelayRef.current);
  //     mediaItemsRef.current.forEach((item) => {
  //       if (item.blobUrl.startsWith("blob:")) {
  //         URL.revokeObjectURL(item.blobUrl);
  //       }
  //       if (item.thumbnail && item.thumbnail.startsWith("blob:")) {
  //         URL.revokeObjectURL(item.thumbnail);
  //       }
  //     });
  //   };
  // }, []);
  useEffect(() => {
    const mediaItemsSnapshot = Array.from(mediaItemsRef.current.values());
    return () => {
      mountedRef.current = false;
      if (searchDelayRef.current) clearTimeout(searchDelayRef.current);
      mediaItemsSnapshot.forEach((item) => {
        if (item.blobUrl.startsWith("blob:")) {
          URL.revokeObjectURL(item.blobUrl);
        }
        if (item.thumbnail && item.thumbnail.startsWith("blob:")) {
          URL.revokeObjectURL(item.thumbnail);
        }
      });
    };
  }, []);

  const generateVideoThumbnail = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.currentTime = 1;

      video.onloadeddata = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) resolve(URL.createObjectURL(blob));
          else reject(new Error("Failed to generate thumbnail"));
        }, "image/jpeg", 0.8);
      };

      video.onerror = () => reject(new Error("Failed to load video"));
      video.src = URL.createObjectURL(file);
    });
  }, []);

  const updateMediaItems = useCallback(() => {
    const mediaArray = Array.from(mediaItemsRef.current.values()).sort((a, b) => a.insertIndex - b.insertIndex);
    onMediaChange?.(mediaArray);
  }, [onMediaChange]);

  const prepareFormDataCallback = useCallback((baseFormData?: FormData) => {
    const formData = baseFormData || new FormData();
    if (mediaItemsRef.current.size === 0) return formData;

    mediaItemsRef.current.forEach((mediaItem, mediaId) => {
      try {
        if (mediaItem.blobUrl.startsWith("blob:")) {
          formData.append(`content_media_${mediaId}`, mediaItem.file);
          formData.append(`content_media_${mediaId}_type`, mediaItem.type);
          formData.append(`content_media_${mediaId}_index`, mediaItem.insertIndex.toString());
          formData.append(`content_media_${mediaId}_timestamp`, mediaItem.timestamp.toString());
          if (mediaItem.type === "video" && mediaItem.thumbnail) {
            formData.append(`content_media_${mediaId}_thumbnail_url`, mediaItem.thumbnail);
          }
        }
      } catch (error) {
        console.error(`Error adding media item ${mediaId} to FormData:`, error);
      }
    });

    return formData;
  }, []);

  useEffect(() => {
    if (onPrepareFormData && typeof onPrepareFormData === "function") {
      onPrepareFormData(prepareFormDataCallback);
    }
  }, [onPrepareFormData, prepareFormDataCallback]);

  useLayoutEffect(() => {
    if (!autoGrow) return;
    resizeEditor();
  }, [autoGrow, props.value, resizeEditor]);

  // Inject styles with sticky toolbar and scrollable content
  useEffect(() => {
    const style = document.createElement("style");
    const containerStyles = autoGrow
      ? `
      .article-text-editor--auto-grow .ql-container.ql-snow {
        height: auto !important;
        overflow-y: visible !important;
      }
      .article-text-editor--auto-grow .ql-editor {
        overflow-y: hidden;
      }
    `
      : `
      .article-text-editor--fixed .ql-container.ql-snow {
        height: calc(${editorHeight} - 42px) !important;
        overflow-y: auto !important;
      }
    `;

    style.textContent = `
      ${containerStyles}

      .article-text-editor .ql-toolbar.ql-snow {
        position: sticky;
        top: 0;
        z-index: 10;
        background: white;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        max-width: 100%;
        box-sizing: border-box;
      }

      .article-text-editor .ql-snow .ql-picker.ql-header{
        width: 86px;
      }

      .article-text-editor .ql-toolbar.ql-snow .ql-formats {
        display: inline-flex;
        align-items: center;
        margin-right: 8px;
        padding-right: 8px;
        position: relative;
      }
      .article-text-editor .ql-toolbar.ql-snow .ql-formats:not(:last-child)::after {
        content: "";
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        width: 1px;
        height: 16px;
        background: #ffe6f4;
      }
      .article-text-editor .ql-toolbar.ql-snow button svg {
        width: auto;
        height: auto;
        max-width: 16px;
        max-height: 16px;
      }
      .article-text-editor .ql-toolbar.ql-snow button {
        width: 24px;
        height: 24px;
        padding: 0;
      }

      @media only screen and (max-width: 767px) {
        .article-text-editor .ql-toolbar.ql-snow {
          padding: 8px 6px;
          row-gap: 4px;
        }
        .article-text-editor .ql-snow .ql-picker.ql-header {
          width: 86px;
        }
        .article-text-editor .ql-toolbar.ql-snow .ql-formats {
          margin-right: 8px;
          padding-right: 8px;
        }
        .article-text-editor .ql-toolbar.ql-snow .ql-formats:not(:last-child)::after {
          height: 16px;
        }
        .article-text-editor .ql-toolbar.ql-snow button {
          width: 22px;
          height: 22px;
        }
        .article-text-editor .ql-toolbar.ql-snow button svg {
          max-width: 14px;
          max-height: 14px;
        }
      }

      .article-text-editor .ql-tooltip {
        display: none !important;
      }

      .article-text-editor .ql-picker.ql-header .ql-picker-label svg {
        display: none;
      }
      .article-text-editor .ql-picker.ql-header .ql-picker-label::after {
        content: "";
        position: absolute;
        top: 50%;
        right: 2px;
        transform: translateY(-50%);
        width: 18px;
        height: 18px;
        background: url("${downArrow}") center / contain no-repeat;
      }

      .article-text-editor .quill {
        position: relative;
      }

      /*
       * Inline media must take up its own row. The article stylesheet applies a
       * max-height/aspect-ratio to image wrappers; that lets the image paint
       * outside its layout box and the following paragraph flows beside it.
       */
      .article-text-editor .ql-media-wrapper {
        position: relative;
        display: block !important;
        float: none !important;
        clear: both !important;
        width: 100% !important;
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
        margin: 16px 0;
      }
      .article-text-editor .ql-media-wrapper img,
      .article-text-editor .ql-media-wrapper video {
        width:100%;
        height:auto !important;
        max-height:none !important;
        aspect-ratio:auto !important;
        display:block;
        float:none !important;
        border-radius:4px;
      }
      .article-text-editor .ql-media-wrapper + p {
        display: block;
        clear: both;
        min-height: 1.5em;
      }
      .article-text-editor .ql-media-delete {
        position:absolute; top:12px; right:12px; width:40px; height:40px; border-radius:50%;
        border:1px solid rgba(0,0,0,0.1); background:#fff; display:flex; align-items:center; justify-content:center;
        box-shadow:0 2px 8px rgba(0,0,0,0.1); cursor:pointer; padding:0;
      }
      .article-text-editor .ql-media-edit {
        position:absolute; top:12px; right:60px; width:40px; height:40px; border-radius:50%;
        border:1px solid rgba(0,0,0,0.1); background:#fff; display:flex; align-items:center; justify-content:center;
        box-shadow:0 2px 8px rgba(0,0,0,0.1); cursor:pointer; padding:0;
      }
      .article-text-editor .ql-media-edit svg,
      .article-text-editor .ql-media-delete svg { width:20px; height:20px; pointer-events:none; }
      .article-text-editor .ql-media-edit .image-edit-icon,
      .article-text-editor .ql-media-delete .delete-icon { fill:#ff0990; color:#ff0990; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [editorHeight, autoGrow]);

  useEffect(() => {
    const quill = reactQuillRef.current?.getEditor?.();
    const root = quill?.root as HTMLElement | undefined;
    if (!root) return;

    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      const editBtn = t.closest(".ql-media-edit") as HTMLElement | null;
      if (editBtn) {
        const wrapper = editBtn.closest(".ql-image-wrapper") as HTMLElement | null;
        const mediaId = wrapper?.getAttribute("data-media-id");
        if (mediaId && mediaItemsRef.current.has(mediaId)) setEditingMediaId(mediaId);
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const btn = t.closest(".ql-media-delete") as HTMLElement | null;
      if (!btn) return;

      const wrapper = btn.closest(".ql-media-wrapper") as HTMLElement | null;
      if (!wrapper) return;

      const mediaId = wrapper.getAttribute("data-media-id");
      if (mediaId && mediaItemsRef.current.has(mediaId)) {
        const mediaItem = mediaItemsRef.current.get(mediaId)!;
        if (mediaItem.blobUrl.startsWith("blob:")) URL.revokeObjectURL(mediaItem.blobUrl);
        if (mediaItem.originalBlobUrl && mediaItem.originalBlobUrl !== mediaItem.blobUrl && mediaItem.originalBlobUrl.startsWith("blob:")) {
          URL.revokeObjectURL(mediaItem.originalBlobUrl);
        }
        if (mediaItem.thumbnail && mediaItem.thumbnail.startsWith("blob:")) URL.revokeObjectURL(mediaItem.thumbnail);
        mediaItemsRef.current.delete(mediaId);
        updateMediaItems();
      }

      const blot: any = Quill.find(wrapper);
      if (blot && typeof blot.remove === "function") blot.remove();
      else wrapper.remove();

      e.preventDefault();
      e.stopPropagation();
    };

    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [updateMediaItems]);

  const handleInlineImageEdit = useCallback((
    file: File | null,
    originalFile?: File | null,
    _type?: "image" | "video",
    _thumbnail?: string,
    editHistory?: InlineMediaEditHistory
  ) => {
    if (!file || !editingMediaId) return;
    const current = mediaItemsRef.current.get(editingMediaId);
    if (!current) return;

    const nextBlobUrl = URL.createObjectURL(file);
    // Retain the initial blob while the media exists: ArticleDropZone uses it to
    // reopen the image from its unedited source on subsequent edits.
    if (current.blobUrl !== current.originalBlobUrl && current.blobUrl.startsWith("blob:")) {
      URL.revokeObjectURL(current.blobUrl);
    }
    mediaItemsRef.current.set(editingMediaId, {
      ...current,
      file,
      blobUrl: nextBlobUrl,
      originalFile: current.originalFile || originalFile || current.file,
      originalBlobUrl: current.originalBlobUrl || current.blobUrl,
      editHistory,
      timestamp: Date.now(),
    });

    const quill = reactQuillRef.current?.getEditor?.();
    const image = (quill?.root as HTMLElement | undefined)?.querySelector(
      `.ql-image-wrapper[data-media-id="${editingMediaId}"] img`
    ) as HTMLImageElement | null;
    if (image) image.src = nextBlobUrl;
    updateMediaItems();
    setEditingMediaId(null);
  }, [editingMediaId, updateMediaItems]);

  const fetchMentionValues = useCallback((search: string): Promise<Mention[]> => {
    if (searchDelayRef.current) clearTimeout(searchDelayRef.current);
    return new Promise((resolve) => {
      searchDelayRef.current = setTimeout(async () => {
        try {
          const apiData: any = await getMentioned(search);
          const responseData = _.get(apiData, "data.data", []);
          resolve(responseData);
        } catch {
          resolve([]);
        }
      }, 400);
    });
  }, []);

  const mediaHandler = useCallback(function (this: any) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,video/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const type = (file.type || "").toLowerCase();
      const isImage = type.startsWith("image/");
      const isVideo = type.startsWith("video/");

      if (isImage && file.size > 25 * 1024 * 1024) {
        FlashMessage("Image is too large. Maximum size allowed is 25MB.", "error");
        return;
      }

      if (isVideo && file.size > 10 * 1024 * 1024) {
        FlashMessage("Video is too large. Maximum size allowed is 10MB.", "error");
        return;
      }

      if (!isImage && !isVideo) {
        FlashMessage("Please select a valid image or video file.", "error");
        return;
      }

      if (!mountedRef.current) return;
      const quill = reactQuillRef.current?.getEditor?.();
      if (!quill) return;

      try {
        const mediaId = `media_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const blobUrl = URL.createObjectURL(file);
        const range = quill.getSelection(true);
        const insertIndex = range ? range.index : quill.getLength();

        let thumbnail: string | undefined;
        if (isVideo) {
          try {
            thumbnail = await generateVideoThumbnail(file);
          } catch (error) {
            console.warn("Failed to generate video thumbnail:", error);
          }
        }

        const mediaItem: MediaItem = {
          id: mediaId,
          file,
          blobUrl,
          type: isVideo ? "video" : "image",
          insertIndex,
          timestamp: Date.now(),
          thumbnail,
          originalFile: isImage ? file : undefined,
          originalBlobUrl: isImage ? blobUrl : undefined,
        };

        mediaItemsRef.current.set(mediaId, mediaItem);
        updateMediaItems();

        const embedValue = { url: blobUrl, mediaId };
        quill.insertEmbed(insertIndex, isVideo ? "mediaVideo" : "mediaImage", embedValue, "user");
        // A block embed occupies one Quill character. Add a paragraph directly
        // after it and place the caret there, rather than leaving the selection
        // on the embed (which makes subsequent text appear over the media).
        quill.insertText(insertIndex + 1, "\n", "user");
        quill.setSelection(insertIndex + 2, 0, "user");
      } catch (error) {
        console.error("Error processing media file:", error);
        FlashMessage("Error processing file. Please try again.", "error");
      }
    };
    input.click();
  }, [generateVideoThumbnail, updateMediaItems]);

  const modules = useMemo(() => {
    const base: any = {
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "underline", "italic", "strike"],
          [{ list: "bullet" }, { list: "ordered" }],
          ["link", "image"],
        ],
        handlers: { image: mediaHandler },
      },
    };

    if (useMention) {
      base.mention = {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ["@"],
        dataAttributes: ["disabled"],
        showDenotationChar: true,
        positioningStrategy: "fixed",
        source: async (
          searchTerm: string,
          renderList: (items: Mention[], search: string) => void,
          mentionChar: string
        ) => {
          if (mentionChar !== "@") return renderList([], searchTerm);
          const data = await fetchMentionValues(searchTerm || "");
          renderList(data, searchTerm);
          props.setOverflow?.(data.length > 0);
        },
      };
    }
    return base;
  }, [mediaHandler, fetchMentionValues, props, useMention]);

  useEffect(() => {
    if (!autoGrow) return;
    const quill = reactQuillRef.current?.getEditor?.();
    if (!quill) return;
    const editor = quill.root as HTMLElement;

    const handleTextChange = () => resizeEditor();
    // Image dimensions are not available at insert time. Recalculate after the
    // browser loads an image (or video metadata), otherwise a later upload can
    // remain clipped at the editor's previous height.
    const handleMediaLoad = () => requestAnimationFrame(resizeEditor);
    quill.on("text-change", handleTextChange);
    editor.addEventListener("load", handleMediaLoad, true);
    editor.addEventListener("loadedmetadata", handleMediaLoad, true);
    resizeEditor();

    return () => {
      quill.off("text-change", handleTextChange);
      editor.removeEventListener("load", handleMediaLoad, true);
      editor.removeEventListener("loadedmetadata", handleMediaLoad, true);
    };
  }, [autoGrow, resizeEditor, modules]);

  useEffect(() => {
    const quill = reactQuillRef.current?.getEditor?.();
    const toolbarEl = quill?.getModule("toolbar")?.container as HTMLElement | undefined;
    const btn = toolbarEl?.querySelector("button.ql-image") as HTMLButtonElement | null;
    if (btn) btn.setAttribute("title", "Media (image/video)");
  }, [modules]);

  useEffect(() => {
    const quill = reactQuillRef.current?.getEditor?.();
    if (!quill) return;

    quill.root.setAttribute("autocapitalize", "off");
    quill.root.setAttribute("autocorrect", "off");

    const unbindLink = bindQuillLinkDialog(quill, setLinkDialog);
    const isPlainUrl = (value: string) => /^https?:\/\/\S+$/i.test(value.trim());

    const handlePaste = (event: ClipboardEvent) => {
      const pastedText = event.clipboardData?.getData("text/plain")?.trim() || "";

      // Some browsers include text/html even for plain URL paste.
      // If plain text itself is a URL, force insert as a link.
      if (!pastedText || !isPlainUrl(pastedText)) return;

      event.preventDefault();
      event.stopPropagation();
      const selection = quill.getSelection(true);
      const index = selection?.index ?? quill.getLength();

      if (selection?.length) {
        quill.deleteText(selection.index, selection.length, "user");
      }

      quill.insertText(index, pastedText, "link", pastedText, "user");
      quill.setSelection(index + pastedText.length, 0, "silent");
    };

    quill.root.addEventListener("paste", handlePaste, true);

    return () => {
      unbindLink();
      quill.root.removeEventListener("paste", handlePaste, true);
    };
  }, [modules]);

  useEffect(() => {
    const quill = reactQuillRef.current?.getEditor?.();
    if (!quill) return;
    const Delta: any = Quill.import("delta");

    quill.clipboard.addMatcher("div.ql-media-wrapper", (node: Element, delta: any) => {
      const htmlEl = node as HTMLElement;
      const hasImg = !!htmlEl.querySelector("img");
      const hasVideo = !!htmlEl.querySelector("video");
      const mediaId = htmlEl.getAttribute("data-media-id") || `media_${Date.now()}`;
      if (hasImg) {
        const img = htmlEl.querySelector("img") as HTMLImageElement | null;
        const url = img?.getAttribute("src") || "";
        return new Delta().insert({ mediaImage: { url, mediaId } });
      }
      if (hasVideo) {
        const vid = htmlEl.querySelector("video") as HTMLVideoElement | null;
        const url = vid?.getAttribute("src") || "";
        return new Delta().insert({ mediaVideo: { url, mediaId } });
      }
      return delta;
    });
  }, []);

  useEffect(() => {
    if (!AIflag || props.value === undefined) return;
    const quill = reactQuillRef.current?.getEditor?.();
    if (!quill) return;

    ignoreNextChangeRef.current = true;
    const html = props.value || "";
    const range = quill.getSelection(true);
    const index = range ? range.index : quill.getLength();

    quill.clipboard.dangerouslyPasteHTML(index, html, "user");
    quill.setSelection(index + 1, 0, "user");
  }, [AIflag, props.value]);

  const isEmptyContent = (content: string) => {
    const initValues = [
      "<p><br></p>",
      "<p><br></p><p><br></p>",
      "<h1><br></h1>",
      "<h2><br></h2>",
      "<h3><br></h3>",
      "<ol><li><br></li></ol>",
      "<ul><li><br></li></ul>",
    ];
    return initValues.some((v) => v.trim() === content.trim());
  };

  useEffect(() => {
    if (initializedRef.current) return;
    const quill = reactQuillRef.current?.getEditor?.();
    if (!quill) return;

    const itemsToLoad = (initialMediaItems && initialMediaItems.length > 0) ? initialMediaItems : [];
    if (itemsToLoad.length > 0) {
      mediaItemsRef.current.clear();
      itemsToLoad.forEach((item) => {
        mediaItemsRef.current.set(item.id, item);
      });
      updateMediaItems();
    }
    initializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMediaItems, contentMediaProcessed]);

  const handleEditorChange = (
    content: string,
    onValueChange?: (v: string) => void
  ) => {
    if (ignoreNextChangeRef.current) {
      ignoreNextChangeRef.current = false;
      return;
    }
    onValueChange?.(isEmptyContent(content) ? "" : content);
    requestAnimationFrame(() => resizeEditor());
  };

  const ControlledEditor = (fieldValue: string, fieldOnChange: (v: string) => void) => (
    <ReactQuill
      ref={reactQuillRef}
      value={fieldValue || ""}
      placeholder={placeholder}
      modules={modules}
      onChange={(content: string) => handleEditorChange(content, fieldOnChange)}
    />
  );

  const UncontrolledEditor = (propValue: string, onChange?: (v: string) => void) => (
    <ReactQuill
      ref={reactQuillRef}
      value={propValue || ""}
      placeholder={placeholder}
      modules={modules}
      className="wysiwyg-data"
      onChange={(content: string) => handleEditorChange(content, onChange)}
    />
  );

  const editorWrapperClass = `article-text-editor ${autoGrow ? "article-text-editor--auto-grow" : "article-text-editor--fixed"
    } rich-text-editor ${className}`.trim();

  const getAlignClass = (align: "left" | "center" | "right") =>
    align === "center" || align === "right" ? `align-${align}` : "";

  return (
    <>
      {editingMediaId && mediaItemsRef.current.get(editingMediaId)?.type === "image" && (() => {
        const media = mediaItemsRef.current.get(editingMediaId)!;
        return (
          <ArticleDropZone
            key={`${media.id}-${media.blobUrl}`}
            allowFileTypes={["png", "jpg", "jpeg", "gif", "webp"]}
            allowFileSize={25 * 1024 * 1024}
            existingImage={media.blobUrl}
            originalImage={media.originalBlobUrl || media.blobUrl}
            existingMediaType="image"
            existingEditHistory={media.editHistory}
            autoOpenEdit
            onSuccess={handleInlineImageEdit}
            onClose={() => setEditingMediaId(null)}
            onError={(error) => FlashMessage(error.message, "error")}
          />
        );
      })()}
      {labelText && (
        <label htmlFor={name} className={labelClassName}>
          {labelText}
        </label>
      )}

      <div className={editorWrapperClass}>
        {control ? (
          <Controller
            name={name}
            control={control}
            rules={props.rules || {}}
            defaultValue={props.defaultValue || ""}
            render={({ field }) => (
              <>
                {ControlledEditor(field.value, field.onChange)}
                {showCount && (
                  <p
                    className={`mt-2 global-counter ${getAlignClass(counterAlign)} ${stripHTMLTags(field.value || "").length > maxLimit ? "error-text" : ""
                      }`}
                  >
                    {`${stripHTMLTags(field.value || "").length}/${maxLimit}`}
                  </p>
                )}
              </>
            )}
          />
        ) : (
          <>
            {UncontrolledEditor(props.value || "", props.onChange)}
            {showCount && (
              <p
                className={`mt-2 global-counter ${getAlignClass(counterAlign)} ${stripHTMLTags(props.value || "").length > maxLimit ? "error-text" : ""
                  }`}
              >
                {`${stripHTMLTags(props.value || "").length}/${maxLimit}`}
              </p>
            )}
          </>
        )}
      </div>

      {errorMessage && showError && <ErrorTextHelper text={errorMessage} />}

      <QuillLinkDialog payload={linkDialog} onClose={() => setLinkDialog(null)} />
    </>
  );
};

export default ArticleTextEditor;
